import { useLocation, useNavigate } from "react-router-dom";
import { useRef } from "react";
import { useCssHeight } from "../lib/useCssHeight";

// The familiar bottom tab bar: Home · Leaderboard · Share · You.
//
// Deliberately the most ordinary navigation there is. The crews using this run
// from cousins to a ministry group, so there is nothing to learn here: it is the
// bar every other app on their phone already has. Four tabs, never five, because
// five is where "simple" starts to go.
//
// Share is the raised centre button, the way create sits in Instagram and
// YouTube, because giving the crew an update is the one contribution the whole
// game runs on.

type Tab = { key: string; label: string; path: string; icon: JSX.Element };

// Tabler-style line icons, inline so there is no icon package to load and they
// inherit currentColor in both themes.
const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 1.75, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };

const HomeIcon = (
  <svg viewBox="0 0 24 24" width="24" height="24" {...stroke} aria-hidden="true">
    <path d="M5 12H3l9-9 9 9h-2" /><path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    <path d="M9 21v-6h6v6" />
  </svg>
);
const TrophyIcon = (
  <svg viewBox="0 0 24 24" width="24" height="24" {...stroke} aria-hidden="true">
    <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4z" />
    <path d="M17 5h2a2 2 0 0 1 0 4h-2M7 5H5a2 2 0 0 0 0 4h2" />
  </svg>
);
const PlusIcon = (
  <svg viewBox="0 0 24 24" width="26" height="26" {...stroke} strokeWidth={2.25} aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const YouIcon = (
  <svg viewBox="0 0 24 24" width="24" height="24" {...stroke} aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" /><path d="M5 20c0-3.3 3.1-5.5 7-5.5s7 2.2 7 5.5" />
  </svg>
);

export function TabBar({ crewId }: { crewId: string }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const base = `/league/${crewId}`;
  const bar = useRef<HTMLElement>(null);

  // Tell the page how tall the bar really is. Screens used to clear it with a
  // fixed 84px, and the bar is taller than that whenever the phone's text size is
  // up, which hid the last button underneath it. The measured height includes
  // the home-indicator gap, because that is padding on the bar itself.
  useCssHeight(bar, "--tabbar-h");

  const tabs: Tab[] = [
    { key: "home", label: "Home", path: base, icon: HomeIcon },
    { key: "leaderboard", label: "Leaderboard", path: `${base}/leaderboard`, icon: TrophyIcon },
    { key: "share", label: "Share", path: `${base}/checkin`, icon: PlusIcon },
    { key: "you", label: "You", path: `${base}/you`, icon: YouIcon },
  ];

  // Home is only "current" on an exact match. Without that it would light up on
  // every screen underneath it, since every path starts with the crew's base.
  const isCurrent = (t: Tab) =>
    t.path === base ? pathname === base || pathname === `${base}/` : pathname.startsWith(t.path);

  return (
    <nav ref={bar} className="tabbar" aria-label="Main">
      {tabs.map((t) => {
        const current = isCurrent(t);
        const share = t.key === "share";
        return (
          <button
            key={t.key}
            className={`tab${current ? " current" : ""}${share ? " share" : ""}`}
            aria-current={current ? "page" : undefined}
            onClick={() => navigate(t.path)}
          >
            <span className="tab-icon">{t.icon}</span>
            <span className="tab-label">{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
